import { useMemo, useState } from "react"
import { Link, Navigate, useParams } from "react-router-dom"
import { useData } from "../data/DataContext"
import { ObjectiveModal } from "../components/ObjectiveModal"
import {
  AddObjectiveCard,
  ObjectiveCard,
  RagCountRing,
  buildRagIndex,
  countRags,
  getCurrentRag,
  getHistoryScores,
  reportingYearForDate,
} from "../components/ObjectiveCard"
import type { Objective, ObjectiveStatus } from "../data/types"

const STATUS_ORDER: Record<ObjectiveStatus, number> = { Active: 0, Draft: 1, Deleted: 2 }

type ModalState =
  | { kind: "none" }
  | { kind: "edit"; objective: Objective }
  | { kind: "create" }

export function PersonDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { people, objectives, ragScores, categories: taxonomyCategories, me, refresh } = useData()
  const [modal, setModal] = useState<ModalState>({ kind: "none" })

  const personId = Number(id)
  const person = people.find((p) => p.id === personId)
  const reportingYear = reportingYearForDate()

  // Sort by taxonomy order — anything not in the taxonomy sinks to the bottom.
  const categoryOrder = useMemo(
    () => taxonomyCategories.map((c) => c.name),
    [taxonomyCategories],
  )
  const categoryIndex = useMemo(() => {
    const m = new Map<string, number>()
    categoryOrder.forEach((c, i) => m.set(c, i))
    return m
  }, [categoryOrder])

  const sortObjectives = (a: Objective, b: Objective): number => {
    const sa = a.status ? STATUS_ORDER[a.status] : 99
    const sb = b.status ? STATUS_ORDER[b.status] : 99
    if (sa !== sb) return sa - sb
    const ca = a.category ? categoryIndex.get(a.category) ?? 99 : 99
    const cb = b.category ? categoryIndex.get(b.category) ?? 99 : 99
    if (ca !== cb) return ca - cb
    return (a.year ?? 0) - (b.year ?? 0)
  }

  const personObjectives = useMemo(() => {
    if (!person) return []
    return objectives
      .filter((o) => o.personName === person.fullName && o.status !== "Deleted")
      .sort(sortObjectives)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectives, person, categoryIndex])

  const categories = useMemo(() => {
    // Taxonomy order first, then in-use-but-not-in-taxonomy stragglers.
    const inUse = new Set<string>()
    for (const o of objectives) if (o.category) inUse.add(o.category)
    const taxonomy = new Set(categoryOrder)
    const extras = [...inUse].filter((c) => !taxonomy.has(c)).sort()
    return [...categoryOrder, ...extras]
  }, [objectives, categoryOrder])

  const ragIndex = useMemo(
    () => buildRagIndex(ragScores, reportingYear),
    [ragScores, reportingYear],
  )

  const counts = useMemo(
    () => countRags(personObjectives, ragIndex),
    [personObjectives, ragIndex],
  )

  if (!person) {
    return <Navigate to="/team" replace />
  }

  const canAdd = me.canEditFor(person.fullName)
  const isSelf = me.matchedPerson?.id === person.id
  const yearLabel = `${reportingYear}/${(reportingYear + 1) % 100}`

  const teamPill =
    person.team === "Exec"
      ? "bg-[#E8E1FC] text-[#4B3F90]"
      : person.team === "CLT"
        ? "bg-[rgb(185_138_58/0.12)] text-brass"
        : "bg-rag-unset-bg text-ink-3"

  return (
    <div>
      {/* Back link */}
      <Link
        to="/team"
        className="mb-[18px] inline-flex items-center gap-[6px] font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-3 transition hover:text-brass"
      >
        <span aria-hidden>&larr;</span> Team dashboard
      </Link>

      {/* Hero */}
      <section className="mb-[26px] flex items-start justify-between gap-[30px] rounded-lg border border-rule bg-card px-[26px] py-[22px] shadow-card">
        <div className="min-w-0">
          <div className="mb-[6px] flex items-center gap-[10px]">
            <h1 className="font-display text-[28px] font-medium leading-[1.1] tracking-[-0.015em] text-ink">
              {person.fullName}
            </h1>
            {isSelf ? (
              <span className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-brass">
                you
              </span>
            ) : null}
            {person.team ? (
              <span
                className={`rounded-full px-[9px] py-[2px] text-[11px] font-medium ${teamPill}`}
              >
                {person.team}
              </span>
            ) : null}
          </div>
          {person.jobTitle ? (
            <div className="text-[14px] text-ink-2">{person.jobTitle}</div>
          ) : null}
          <div className="mt-[6px] font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
            {person.department ?? "No department"} &middot; {yearLabel} &middot;{" "}
            {personObjectives.length} objective{personObjectives.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="self-stretch w-px bg-rule" />

        <div className="flex-shrink-0 text-right">
          <RagCountRing counts={counts} />
          <div className="mt-2 font-mono text-[10px] tracking-[0.04em] text-ink-3">
            {isSelf ? "your current status" : "current status"}
          </div>
        </div>
      </section>

      {/* Permission note for viewers */}
      {me.role === "Viewer" ? (
        <div className="mb-4 rounded-md border border-rule bg-paper-sunk px-[14px] py-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink-3">
          Read-only access
        </div>
      ) : null}

      {/* Cards */}
      {personObjectives.length === 0 && !canAdd ? (
        <div className="rounded-lg border border-dashed border-rule-strong bg-card p-8 text-center">
          <div className="mb-2 font-display text-[17px] font-medium text-ink">
            No objectives recorded
          </div>
          <p className="text-[13px] text-ink-2">
            {isSelf
              ? "You haven't created any objectives yet."
              : `${person.fullName} has no objectives recorded for ${yearLabel}.`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-[18px]">
          {personObjectives.map((o) => (
            <ObjectiveCard
              key={o.id}
              objective={o}
              rag={getCurrentRag(o, ragIndex)}
              scores={getHistoryScores(o, ragIndex)}
              reportingYear={reportingYear}
              editable={me.canEdit(o)}
              onClick={() => setModal({ kind: "edit", objective: o })}
            />
          ))}
          {canAdd ? <AddObjectiveCard onClick={() => setModal({ kind: "create" })} /> : null}
        </div>
      )}

      {/* Modals */}
      {modal.kind === "edit" ? (
        <ObjectiveModal
          mode="edit"
          objective={modal.objective}
          categories={categories}
          onClose={() => setModal({ kind: "none" })}
          onSaved={refresh}
        />
      ) : null}
      {modal.kind === "create" ? (
        <ObjectiveModal
          mode="create"
          personName={person.fullName}
          defaultYear={reportingYear}
          categories={categories}
          onClose={() => setModal({ kind: "none" })}
          onSaved={refresh}
        />
      ) : null}
    </div>
  )
}
